import { parseReceivedHeaders , Conflict } from "./utils";
import invariant from "invariant";
import {
	REQUEST_CREATE,
	REQUEST_RETRIEVE,
	REQUEST_LIST,
	REQUEST_TOTAL_COUNT,
	REQUEST_UPDATE_ONE,
	REQUEST_MANY,
	REQUEST_DELETE_ONE,
	SET_CURRENT_ID,
	SET_CURRENT,
	RESET,
	SET_SEARCH,
	CLEAR_SEARCH,
	SET_QUERY,
	CLEAR_QUERY,
	NEXT_PAGE,
} from './types';

function mergeIndex(oldIndex, instances) {
	if (!instances.length) {
		return oldIndex;
	}

	return instances.reduce((index, value) => {
		index[value.getKey()] = value;
		return index;
	}, Object.assign({}, oldIndex));
}

function replaceIn(values, instance) {
	if (values) {
		const instanceIndex = values.findIndex((value) => value.getKey() === instance.getKey());
		if (instanceIndex !== -1) {
			// copy and replace the instance in the values
			values = values.slice();
			values.splice(instanceIndex, 1, instance);
		}
	}
	return values;
}

function replaceInstance(state, instance) {
	const { current, currentID, index, values } = state;
	return {
		...state,
		index: mergeIndex(index, [instance]),
		values: replaceIn(values, instance),
		current: (currentID !== null && currentID === instance.getKey()) ? instance : current,
	};
}

const reduceFunctions = {
	[SET_CURRENT_ID]: (state, { payload }) => {
		const { id } = payload;
		const { current } = state;
		return {
			...state,
			current: id !== null && current && current.getKey() === id ? current : null,
			currentID: id,
		};
	},
	[SET_CURRENT]: (state, { payload }) => {
		const { instance } = payload;
		const currentID = instance ? instance.getKey() : null;

		return {
			...state,
			currentID,
			current: instance,
			index: instance ? mergeIndex(state.index, [instance]) : state.index,
		};
	},
	[REQUEST_RETRIEVE]: (state, { data, payload, success }) => {
		if (success === null) {
			return ({
				...state,
				currentLoading: true,
			});
		}
		if (!success) {
			return ({
				...state,
				currentLoading: false,
			});
		}

		const instance = new payload.Model(data, payload.id);
		return {
			...state,
			current: instance.getKey() === state.currentID ? instance : state.current,
			currentLoading: false,
			index: mergeIndex(state.index, [instance]),
		};
	},

	[REQUEST_TOTAL_COUNT]: (state, { data, success, payload }) => {
		if (success === null) {
			if (state.abort) {
				state.abort();
			}
			
			return {
				...state,
				loading: true,
				success: success,
			};
		}
		if (!success) {
			return {
				...state,
				loading: false,
				success: success,
			};
		}

		const { Model } = payload;
		const modelInstance = data ? new Model(data) : null;
		const totalCount = modelInstance ? modelInstance.getTotal() : 0;

		return {
			...state,
			total: totalCount,
			loading: false,
			success: success,
		};
	},
	
	[REQUEST_LIST]: (state, { data, abort, payload, response, success, request }) => {
		if (success === null) {
			if (state.abort) {
				state.abort();
			}

			return {
				...state,
				abort,
				request,
				loading: true,
				nextURL: undefined,
				error: undefined
			};
		}

		if (state.request !== request) {
			// request has been replaced by another one
			return state;
		}

		const nextState = {
			...state,
			request: undefined,
			abort: undefined,
			loading: false,
			error: response ? response.status : undefined
		};

		if (!success) {
			return {
				...nextState,
				...(!payload.append && {
					values: [],
					next: undefined,
					total: undefined,
				})
			};
		}

		const { next, total } = parseReceivedHeaders(response.headers);
		if (data.results) {
			//unlayer returns the list in the result key
			data = data.results;
		}
		invariant(data instanceof Array, 'data is not a list of objects');
		const instances = data.map((value) => new payload.Model(value)); 
		const nextValues = (payload.append && state.values) ? state.values.concat(instances) : instances;

		return {
			...nextState,
			next,
			total,
			empty: nextValues.length === 0,
			values: nextValues,
			newValues: instances,
			index: mergeIndex(state.index, instances),
		};
	},
	[NEXT_PAGE]: (state, { payload }) => ({
		...state,
		nextURL: payload.next,
		offset: payload.next ? undefined : (state.offset || 0) + payload.limit,
	}),
	[REQUEST_UPDATE_ONE]: (state, { data, payload, success }) => {
		if (success === null) {
			return {
				...state,
				updating: true,
			};
		}
		if (!success) {
			return {
				...state,
				updating: false,
			};
		}

		const instance = new payload.Model(data);
		return replaceInstance({
			...state,
			updating: false,
		}, instance);
	},
	[REQUEST_CREATE]: (state, { data, payload, response, success }) => {
		if (success === null) {
			return {
				...state,
				creating: true,
			};
		}
		if (!success) {
			return {
				...state,
				creating: undefined,
				currentID: null,
				current: null,
			};
		}
		if (response.status === 409) {
			return {
				...state,
				creating: undefined,
				currentID: null,
				current: new Conflict(data),
			};
		}

		const instance = new payload.Model(data);
		return {
			...state,
			creating: undefined,
			currentID: instance.getKey(),
			current: instance,
			index: mergeIndex(state.index, [instance]),
		};
	},
	[REQUEST_MANY]: (state, { data, payload, success }) => {
		if (success === null) {
			return {
				...state,
				loadingMany: true,
			};
		}
		if (!success) {
			return {
				...state,
				loadingMany: false,
			};
		}
		const instances = data.map((value) => new payload.Model(value));
		return {
			...state,
			index: mergeIndex(state.index, instances),
			loadingMany: false,
		};
	},
	[REQUEST_DELETE_ONE]: (state, { data, payload, success }) => {
		if (success === null) {
			return {
				...state,
				deleting: true,
			};
		}
		if (!success) {
			return {
				...state,
				deleting: undefined,
			};
		}

		// Remove the old ressource from the state
		// In the 'current'
		const { id, Model } = payload;
		if (data && Model) {
			const instance = new Model(data);
			return replaceInstance({
				...state,
				deleting: undefined,
			}, instance);
		}

		const current = (state.current && state.current.getKey() === id) ? null : state.current;

		// In the 'values'
		const values = state.values ? state.values.filter((value) => value.getKey() !== id) : [];

		// In the 'index'
		const index = { ...state.index }; // copy index
		delete index[id]; // remove the deleted instance if it exists

		return {
			...state,
			current: current,
			currentID: current ? state.currentID : null,
			deleting: undefined,
			index,
			values,
		};
	},
	[CLEAR_SEARCH]: (state) => {
		const { search: _, _allValues, ...oldState } = state;
		return { ...oldState, ..._allValues };
	},
	[SET_SEARCH]: (state, { payload }) => {
		const { values, offset, _allValues, ...oldState } = state;
		return {
			...oldState,
			_allValues: _allValues || (values ? {
				values,
				offset,
			} : undefined),
			search: payload.search,
		};
	},
	[CLEAR_QUERY]: (state) => state.storedState || state,
	[SET_QUERY]: (state, { payload }) => {
		const { query, storedState, ...nextStoredState } = state;
		return {
			storedState: storedState || nextStoredState,
			query: payload.query,
		};
	},
	[RESET]: () => undefined,
};

export default function apiReducer(state, action) {
	const { type, index } = action;
	const reduceFunction = reduceFunctions[type];
	if (!reduceFunction) {
		return state;
	}

	const currentState = (state && state[index]) || {};
	const nextState = reduceFunction(currentState, action);
	if (currentState === nextState) {
		return state;
	}

	return {
		...state,
		[index]: nextState,
	};
}
